import { useState } from 'react';
import { HashRouter, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider } from '@/contexts/AuthContext';
import { CartProvider } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CartDrawer from '@/components/CartDrawer';
import CheckoutModal from '@/components/CheckoutModal';
import ResetPasswordModal from '@/components/ResetPasswordModal';
import Home from '@/pages/Home';
import AdminPanel from '@/pages/AdminPanel';
import FigurineDetailPage from '@/pages/FigurineDetailPage';
import OrdersPage from '@/pages/OrdersPage';
import ProfilePage from '@/pages/ProfilePage';
import GamePage from '@/pages/GamePage';
import VenomGamePage from '@/pages/VenomGamePage';
import PersonalizacjaPage from '@/pages/PersonalizacjaPage';

const queryClient = new QueryClient();

// Globalny komponent obsługujący modal resetu hasła (działa na każdej stronie)
function GlobalModals() {
  const { isPasswordRecovery, clearPasswordRecovery } = useAuth();
  return (
    <ResetPasswordModal
      open={isPasswordRecovery}
      onClose={clearPasswordRecovery}
    />
  );
}

function AppRoutes() {
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isDetail = location.pathname.startsWith('/figurine/');
  const isOrders = location.pathname.startsWith('/orders');
  const isProfile = location.pathname.startsWith('/profile');
  const isGame = location.pathname.startsWith('/game');
  const isVenom = location.pathname.startsWith('/venom');
  const isPersonalizacja = location.pathname.startsWith('/personalizacja');

  const openAuth = (mode: 'login' | 'register') => {
    setAuthMode(mode);
    setAuthOpen(true);
  };

  if (isAdmin) {
    return (
      <Routes>
        <Route path="/admin" element={<AdminPanel />} />
      </Routes>
    );
  }

  if (isDetail) {
    return (
      <>
        <Routes>
          <Route path="/figurine/:slug" element={<FigurineDetailPage onCartOpen={() => setCartOpen(true)} />} />
        </Routes>
        <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} onCheckout={() => setCheckoutOpen(true)} />
        <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
      </>
    );
  }

  if (isOrders) {
    return (
      <Routes>
        <Route path="/orders" element={<OrdersPage />} />
      </Routes>
    );
  }

  if (isProfile) {
    return (
      <Routes>
        <Route path="/profile" element={<ProfilePage />} />
      </Routes>
    );
  }

  if (isGame) {
    return (
      <Routes>
        <Route path="/game" element={<GamePage />} />
      </Routes>
    );
  }

  if (isVenom) {
    return (
      <Routes>
        <Route path="/venom" element={<VenomGamePage />} />
      </Routes>
    );
  }

  if (isPersonalizacja) {
    return (
      <Routes>
        <Route path="/personalizacja" element={<PersonalizacjaPage />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Navbar onAuthOpen={openAuth} onCartOpen={() => setCartOpen(true)} />
      <div className="flex-1 pt-16">
        <Routes>
          <Route
            path="/"
            element={
              <Home
                onAuthOpen={openAuth}
                authOpen={authOpen}
                authMode={authMode}
                onAuthClose={() => setAuthOpen(false)}
                onAuthModeChange={(mode) => setAuthMode(mode)}
              />
            }
          />
        </Routes>
      </div>
      <Footer />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} onCheckout={() => { setCartOpen(false); setCheckoutOpen(true); }} />
      <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
    </div>
  );
}

export default function App() {
  return (
    <div className="dark">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CartProvider>
            <TooltipProvider>
              <HashRouter>
                <AppRoutes />
              </HashRouter>
            </TooltipProvider>
            <GlobalModals />
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: 'oklch(0.16 0.03 280)',
                  border: '1px solid oklch(0.28 0.05 280)',
                  color: 'oklch(0.92 0.015 280)',
                  fontFamily: 'Rajdhani, sans-serif',
                },
              }}
            />
          </CartProvider>
        </AuthProvider>
      </QueryClientProvider>
    </div>
  );
}
